import { gql } from '@apollo/client';
import { chakra, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { components } from '~components/ui/Select';
import { fromError } from '~utils/errors';
import {
  ConfigSiteSelect_ConfigFragment,
  ConfigSiteSelect_CustomerFragment,
  useConfigUpdateDisplaySiteMutation,
} from './__generated__/ConfigSiteSelect.graphql';

interface Props {
  customer: ConfigSiteSelect_CustomerFragment;
  config: ConfigSiteSelect_ConfigFragment;
  isDisabled?: boolean;
}

interface SiteSelectOption {
  label: string;
  address?: string | null;
  value: string;
}

export function ConfigSiteSelect({ customer, config, isDisabled = false }: Props) {
  // const analytics = useAnalyticsReporter();
  const siteOptions = useMemo(
    () =>
      (customer.sites || []).map<SiteSelectOption>((site) => ({
        label: site.name,
        address: site.address,
        value: site.id,
      })),
    [customer],
  );
  const formatOptionLabel = useCallback((data: SiteSelectOption) => {
    const label = data.label;
    const address = data.address;

    return (
      <chakra.span>
        {label}
        {address && <chakra.span color="gray.500"> - {address}</chakra.span>}
      </chakra.span>
    );
  }, []);
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedSite, setSelectedSite] = useState<SingleValue<SiteSelectOption>>(null);
  const [updateSite, { loading }] = useConfigUpdateDisplaySiteMutation();

  useEffect(() => {
    const site = config.site ?? null;
    const matchedSite = siteOptions.find((option) => option.value === site?.id) || null;
    setSelectedSite(matchedSite);
  }, [config, siteOptions]);

  const handleChange = async (
    option: SiteSelectOption | null,
    meta: ActionMeta<SiteSelectOption>,
  ) => {
    try {
      await updateSite({
        variables: {
          input: {
            configuraionId: config.id,
            siteId: meta.action === 'clear' ? null : option?.value,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      // analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'sites' });
    } catch (err) {
      toast({
        status: 'error',
        title: t('updateDisplaySiteErr'),
        description: fromError(err, 'UpdateDisplaySite'),
      });
    }
  };

  return (
    <Select
      value={selectedSite}
      options={siteOptions}
      components={{
        ...components,
      }}
      formatOptionLabel={formatOptionLabel}
      onChange={handleChange}
      placeholder={t('assignSite')}
      isLoading={loading}
      isDisabled={isDisabled || loading}
      isMulti={false}
      escapeClearsValue={true}
      isClearable={true}
      isSearchable={true}
      menuPlacement="auto"
    />
  );
}

ConfigSiteSelect.graphql = {
  fragments: {
    ConfigSiteSelect_config: gql`
      fragment ConfigSiteSelect_config on ConfigurationModel {
        id
        site {
          id
          name
          address
        }
      }
    `,
    ConfigSiteSelect_customer: gql`
      fragment ConfigSiteSelect_customer on Customer {
        id
        sites {
          id
          name
          address
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateDisplaySite: gql`
      mutation ConfigUpdateDisplaySite($input: ConfigurationUpdateSiteInput!) {
        configurationUpdateSite(input: $input) {
          id
          site {
            id
            name
            address
          }
        }
      }
    `,
  },
};
